import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FORMAT_CATEGORIES, FormatCategory } from '../config/formatSpecs';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}

interface ImageCropEditorProps {
  imageUrl: string;
  targetAspectRatio: number | null; // z.B. 1 für 1:1, 0.8 für 4:5, 0.5625 für 9:16, null für frei
  onCropChange: (cropArea: CropArea) => void;
  initialCrop?: CropArea;
  className?: string;
}

export function ImageCropEditor({
  imageUrl,
  targetAspectRatio,
  onCropChange,
  initialCrop,
  className = '',
}: ImageCropEditorProps) {
  const { t } = useTranslation(['media', 'common']);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Position des Bildes (in Pixeln, relativ zum Container-Zentrum)
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // Zoom-Level (1 = Bild passt genau, >1 = vergrößert)
  const [scale, setScale] = useState(1);

  // Minimaler Zoom: Bild muss die Crop-Box vollständig ausfüllen
  const [minScale, setMinScale] = useState(1);

  // Für freies Zuschneiden: Crop-Box Größe und Position
  const [freeCropBox, setFreeCropBox] = useState({ x: 0, y: 0, width: 200, height: 200 });
  const [isResizing, setIsResizing] = useState<string | null>(null); // 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'
  const [isMovingCropBox, setIsMovingCropBox] = useState(false);
  const [cropBoxDragStart, setCropBoxDragStart] = useState({ x: 0, y: 0 });

  const isFreeMode = targetAspectRatio === null;

  // Berechne die Crop-Box Dimensionen basierend auf Container und Seitenverhältnis
  const getCropBoxDimensions = useCallback(() => {
    if (!containerSize.width || !containerSize.height) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }

    // Im Free-Mode: Nutze die freeCropBox-Werte
    if (isFreeMode) {
      return {
        width: freeCropBox.width,
        height: freeCropBox.height,
        x: freeCropBox.x,
        y: freeCropBox.y,
      };
    }

    const containerRatio = containerSize.width / containerSize.height;
    let boxWidth: number;
    let boxHeight: number;

    if (containerRatio > targetAspectRatio!) {
      // Container ist breiter - Höhe begrenzt
      boxHeight = containerSize.height * 0.85;
      boxWidth = boxHeight * targetAspectRatio!;
    } else {
      // Container ist höher - Breite begrenzt
      boxWidth = containerSize.width * 0.85;
      boxHeight = boxWidth / targetAspectRatio!;
    }

    return { width: boxWidth, height: boxHeight, x: 0, y: 0 };
  }, [containerSize, targetAspectRatio, isFreeMode, freeCropBox]);

  // Berechne minimalen Zoom damit Bild die Crop-Box ausfüllt
  const calculateMinScale = useCallback(() => {
    if (!imageSize.width || !imageSize.height) return 1;

    const cropBox = getCropBoxDimensions();
    if (!cropBox.width || !cropBox.height) return 1;

    // Minimaler Scale: Bild muss Crop-Box vollständig abdecken
    const scaleX = cropBox.width / imageSize.width;
    const scaleY = cropBox.height / imageSize.height;

    return Math.max(scaleX, scaleY);
  }, [imageSize, getCropBoxDimensions]);

  // Begrenze Position damit Bild die Crop-Box ausfüllt
  const constrainPosition = useCallback((x: number, y: number, currentScale: number) => {
    const cropBox = getCropBoxDimensions();
    if (!cropBox.width || !cropBox.height || !imageSize.width || !imageSize.height) {
      return { x: 0, y: 0 };
    }

    const scaledWidth = imageSize.width * currentScale;
    const scaledHeight = imageSize.height * currentScale;

    // Maximale Verschiebung in jede Richtung
    const maxX = (scaledWidth - cropBox.width) / 2;
    const maxY = (scaledHeight - cropBox.height) / 2;

    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, [getCropBoxDimensions, imageSize]);

  // Bild laden
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Container-Größe messen
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // Free-Mode: Initialisiere Crop-Box zentriert
  useEffect(() => {
    if (isFreeMode && containerSize.width && containerSize.height && imageSize.width && imageSize.height) {
      // Berechne die Bildgröße im Container
      const containerAspect = containerSize.width / containerSize.height;
      const imageAspect = imageSize.width / imageSize.height;

      let displayWidth: number;
      let displayHeight: number;

      if (imageAspect > containerAspect) {
        displayWidth = containerSize.width * 0.9;
        displayHeight = displayWidth / imageAspect;
      } else {
        displayHeight = containerSize.height * 0.9;
        displayWidth = displayHeight * imageAspect;
      }

      // Setze initiale Crop-Box auf 70% des Bildes, zentriert
      const boxWidth = displayWidth * 0.7;
      const boxHeight = displayHeight * 0.7;

      setFreeCropBox({
        x: (containerSize.width - boxWidth) / 2,
        y: (containerSize.height - boxHeight) / 2,
        width: boxWidth,
        height: boxHeight,
      });
    }
  }, [isFreeMode, containerSize, imageSize]);

  // Minimalen Zoom berechnen und initialisieren
  useEffect(() => {
    const newMinScale = calculateMinScale();
    setMinScale(newMinScale);

    if (initialCrop?.scale) {
      setScale(Math.max(initialCrop.scale, newMinScale));
      setPosition({ x: initialCrop.x || 0, y: initialCrop.y || 0 });
    } else {
      setScale(newMinScale);
      setPosition({ x: 0, y: 0 });
    }
  }, [calculateMinScale, initialCrop]);

  // Bei Änderung des Seitenverhältnisses neu berechnen
  useEffect(() => {
    const newMinScale = calculateMinScale();
    setMinScale(newMinScale);
    setScale((prev) => Math.max(prev, newMinScale));
    setPosition((prev) => constrainPosition(prev.x, prev.y, Math.max(scale, newMinScale)));
  }, [targetAspectRatio, calculateMinScale, constrainPosition, scale]);

  // Crop-Änderungen nach außen melden
  useEffect(() => {
    const cropBox = getCropBoxDimensions();
    if (!cropBox.width || !cropBox.height || !imageSize.width || !imageSize.height) return;

    // Berechne prozentuale Crop-Area basierend auf Position und Scale
    const scaledWidth = imageSize.width * scale;
    const scaledHeight = imageSize.height * scale;

    // Position des sichtbaren Bereichs im Originalbild
    const visibleX = (scaledWidth / 2 - position.x - cropBox.width / 2) / scale;
    const visibleY = (scaledHeight / 2 - position.y - cropBox.height / 2) / scale;
    const visibleWidth = cropBox.width / scale;
    const visibleHeight = cropBox.height / scale;

    // In Prozent umrechnen
    const cropArea: CropArea = {
      x: (visibleX / imageSize.width) * 100,
      y: (visibleY / imageSize.height) * 100,
      width: (visibleWidth / imageSize.width) * 100,
      height: (visibleHeight / imageSize.height) * 100,
      scale,
    };

    onCropChange(cropArea);
  }, [position, scale, imageSize, getCropBoxDimensions, onCropChange]);

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      setPosition(constrainPosition(newX, newY, scale));
    },
    [isDragging, dragStart, constrainPosition, scale]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
  };

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];

      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;

      setPosition(constrainPosition(newX, newY, scale));
    },
    [isDragging, dragStart, constrainPosition, scale]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleTouchMove, handleTouchEnd]);

  // Zoom ändern
  const handleZoomChange = (newScale: number) => {
    const clampedScale = Math.max(minScale, Math.min(3, newScale));
    setScale(clampedScale);
    // Position anpassen damit Bild die Crop-Box weiterhin ausfüllt
    setPosition((prev) => constrainPosition(prev.x, prev.y, clampedScale));
  };

  // Mausrad-Zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    handleZoomChange(scale + delta);
  };

  // Zentrieren
  const centerImage = () => {
    setPosition({ x: 0, y: 0 });
  };

  // Auf minimalen Zoom zurücksetzen
  const resetZoom = () => {
    setScale(minScale);
    setPosition({ x: 0, y: 0 });
  };

  // ============ Free Crop Mode Handlers ============

  // Start resizing crop box
  const handleResizeStart = (e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(handle);
    setCropBoxDragStart({ x: e.clientX, y: e.clientY });
  };

  // Start moving crop box
  const handleCropBoxMoveStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMovingCropBox(true);
    setCropBoxDragStart({ x: e.clientX - freeCropBox.x, y: e.clientY - freeCropBox.y });
  };

  // Handle resize/move
  const handleFreeCropMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isFreeMode) return;

      const minSize = 50;

      if (isResizing) {
        const dx = e.clientX - cropBoxDragStart.x;
        const dy = e.clientY - cropBoxDragStart.y;

        setFreeCropBox((prev) => {
          let { x, y, width, height } = prev;

          switch (isResizing) {
            case 'se':
              width = Math.max(minSize, prev.width + dx);
              height = Math.max(minSize, prev.height + dy);
              break;
            case 'sw':
              width = Math.max(minSize, prev.width - dx);
              height = Math.max(minSize, prev.height + dy);
              x = prev.x + (prev.width - width);
              break;
            case 'ne':
              width = Math.max(minSize, prev.width + dx);
              height = Math.max(minSize, prev.height - dy);
              y = prev.y + (prev.height - height);
              break;
            case 'nw':
              width = Math.max(minSize, prev.width - dx);
              height = Math.max(minSize, prev.height - dy);
              x = prev.x + (prev.width - width);
              y = prev.y + (prev.height - height);
              break;
            case 'n':
              height = Math.max(minSize, prev.height - dy);
              y = prev.y + (prev.height - height);
              break;
            case 's':
              height = Math.max(minSize, prev.height + dy);
              break;
            case 'e':
              width = Math.max(minSize, prev.width + dx);
              break;
            case 'w':
              width = Math.max(minSize, prev.width - dx);
              x = prev.x + (prev.width - width);
              break;
          }

          // Constrain to container bounds
          x = Math.max(0, Math.min(containerSize.width - width, x));
          y = Math.max(0, Math.min(containerSize.height - height, y));

          return { x, y, width, height };
        });

        setCropBoxDragStart({ x: e.clientX, y: e.clientY });
      }

      if (isMovingCropBox) {
        const newX = e.clientX - cropBoxDragStart.x;
        const newY = e.clientY - cropBoxDragStart.y;

        setFreeCropBox((prev) => ({
          ...prev,
          x: Math.max(0, Math.min(containerSize.width - prev.width, newX)),
          y: Math.max(0, Math.min(containerSize.height - prev.height, newY)),
        }));
      }
    },
    [isFreeMode, isResizing, isMovingCropBox, cropBoxDragStart, containerSize]
  );

  const handleFreeCropMouseUp = useCallback(() => {
    setIsResizing(null);
    setIsMovingCropBox(false);
  }, []);

  useEffect(() => {
    if (isFreeMode && (isResizing || isMovingCropBox)) {
      window.addEventListener('mousemove', handleFreeCropMouseMove);
      window.addEventListener('mouseup', handleFreeCropMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleFreeCropMouseMove);
      window.removeEventListener('mouseup', handleFreeCropMouseUp);
    };
  }, [isFreeMode, isResizing, isMovingCropBox, handleFreeCropMouseMove, handleFreeCropMouseUp]);

  const cropBox = getCropBoxDimensions();
  const zoomPercent = Math.round((scale / minScale) * 100);

  return (
    <div className={`relative ${className}`}>
      {/* Container mit dunklem Hintergrund */}
      <div
        ref={containerRef}
        className="relative overflow-hidden bg-gray-900 rounded-lg"
        style={{ height: '400px' }}
        onWheel={!isFreeMode ? handleWheel : undefined}
      >
        {isFreeMode ? (
          /* ============ FREE CROP MODE ============ */
          <>
            {/* Static image */}
            <img
              src={imageUrl}
              alt={t('media:crop.toCrop')}
              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              draggable={false}
            />

            {/* Dark overlay with cutout for crop box */}
            <div className="absolute inset-0 pointer-events-none">
              {/* SVG mask for complex overlay */}
              <svg className="absolute inset-0 w-full h-full">
                <defs>
                  <mask id="cropMask">
                    <rect width="100%" height="100%" fill="white" />
                    <rect
                      x={freeCropBox.x}
                      y={freeCropBox.y}
                      width={freeCropBox.width}
                      height={freeCropBox.height}
                      fill="black"
                    />
                  </mask>
                </defs>
                <rect
                  width="100%"
                  height="100%"
                  fill="rgba(0,0,0,0.6)"
                  mask="url(#cropMask)"
                />
              </svg>
            </div>

            {/* Moveable/Resizable Crop Box */}
            <div
              className="absolute border-2 border-white cursor-move"
              style={{
                left: freeCropBox.x,
                top: freeCropBox.y,
                width: freeCropBox.width,
                height: freeCropBox.height,
              }}
              onMouseDown={handleCropBoxMoveStart}
            >
              {/* Grid-Linien (Drittel-Regel) */}
              <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white opacity-40 pointer-events-none" />
              <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white opacity-40 pointer-events-none" />
              <div className="absolute top-1/3 left-0 right-0 h-px bg-white opacity-40 pointer-events-none" />
              <div className="absolute top-2/3 left-0 right-0 h-px bg-white opacity-40 pointer-events-none" />

              {/* Resize handles - Corners */}
              <div
                className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border border-gray-400 cursor-nw-resize"
                onMouseDown={(e) => handleResizeStart(e, 'nw')}
              />
              <div
                className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border border-gray-400 cursor-ne-resize"
                onMouseDown={(e) => handleResizeStart(e, 'ne')}
              />
              <div
                className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border border-gray-400 cursor-sw-resize"
                onMouseDown={(e) => handleResizeStart(e, 'sw')}
              />
              <div
                className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border border-gray-400 cursor-se-resize"
                onMouseDown={(e) => handleResizeStart(e, 'se')}
              />

              {/* Resize handles - Edges */}
              <div
                className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-6 h-3 bg-white border border-gray-400 cursor-n-resize rounded-sm"
                onMouseDown={(e) => handleResizeStart(e, 'n')}
              />
              <div
                className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-6 h-3 bg-white border border-gray-400 cursor-s-resize rounded-sm"
                onMouseDown={(e) => handleResizeStart(e, 's')}
              />
              <div
                className="absolute top-1/2 -left-1.5 -translate-y-1/2 w-3 h-6 bg-white border border-gray-400 cursor-w-resize rounded-sm"
                onMouseDown={(e) => handleResizeStart(e, 'w')}
              />
              <div
                className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-6 bg-white border border-gray-400 cursor-e-resize rounded-sm"
                onMouseDown={(e) => handleResizeStart(e, 'e')}
              />
            </div>

            {/* Hinweis-Text */}
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              {t('media:crop.dragFrameHint')}
            </div>
          </>
        ) : (
          /* ============ FIXED ASPECT RATIO MODE ============ */
          <>
            {/* Bewegliches Bild */}
            <div
              className="absolute cursor-move select-none"
              style={{
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
                width: imageSize.width * scale,
                height: imageSize.height * scale,
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <img
                src={imageUrl}
                alt={t('media:crop.toCrop')}
                className="w-full h-full object-contain pointer-events-none"
                draggable={false}
              />
            </div>

            {/* Dunkle Overlay-Bereiche außerhalb der Crop-Box */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Oben */}
              <div
                className="absolute bg-black bg-opacity-60"
                style={{
                  top: 0,
                  left: 0,
                  right: 0,
                  height: `calc(50% - ${cropBox.height / 2}px)`,
                }}
              />
              {/* Unten */}
              <div
                className="absolute bg-black bg-opacity-60"
                style={{
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: `calc(50% - ${cropBox.height / 2}px)`,
                }}
              />
              {/* Links */}
              <div
                className="absolute bg-black bg-opacity-60"
                style={{
                  top: `calc(50% - ${cropBox.height / 2}px)`,
                  left: 0,
                  width: `calc(50% - ${cropBox.width / 2}px)`,
                  height: cropBox.height,
                }}
              />
              {/* Rechts */}
              <div
                className="absolute bg-black bg-opacity-60"
                style={{
                  top: `calc(50% - ${cropBox.height / 2}px)`,
                  right: 0,
                  width: `calc(50% - ${cropBox.width / 2}px)`,
                  height: cropBox.height,
                }}
              />
            </div>

            {/* Crop-Box Rahmen mit Grid */}
            <div
              className="absolute pointer-events-none border-2 border-white"
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: cropBox.width,
                height: cropBox.height,
              }}
            >
              {/* Grid-Linien (Drittel-Regel) */}
              <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white opacity-40" />
              <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white opacity-40" />
              <div className="absolute top-1/3 left-0 right-0 h-px bg-white opacity-40" />
              <div className="absolute top-2/3 left-0 right-0 h-px bg-white opacity-40" />

              {/* Ecken hervorheben */}
              <div className="absolute -top-0.5 -left-0.5 w-4 h-4 border-t-2 border-l-2 border-white" />
              <div className="absolute -top-0.5 -right-0.5 w-4 h-4 border-t-2 border-r-2 border-white" />
              <div className="absolute -bottom-0.5 -left-0.5 w-4 h-4 border-b-2 border-l-2 border-white" />
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 border-b-2 border-r-2 border-white" />
            </div>

            {/* Hinweis-Text */}
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              {t('media:crop.dragImageHint')}
            </div>
          </>
        )}
      </div>

      {/* Zoom-Slider - nur im festen Seitenverhältnis-Modus */}
      {!isFreeMode && (
        <div className="flex items-center gap-4 mt-4 px-2">
          <button
            type="button"
            onClick={() => handleZoomChange(scale - 0.1)}
            disabled={scale <= minScale}
            className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>

          <div className="flex-1 relative">
            <input
              type="range"
              min={minScale * 100}
              max={300}
              value={scale * 100}
              onChange={(e) => handleZoomChange(parseFloat(e.target.value) / 100)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          <button
            type="button"
            onClick={() => handleZoomChange(scale + 0.1)}
            disabled={scale >= 3}
            className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          <span className="text-sm text-gray-600 w-12 text-right">{zoomPercent}%</span>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between mt-3 text-sm">
        <div className="text-gray-500">
          {isFreeMode
            ? t('media:crop.freeMode')
            : targetAspectRatio === 1
            ? t('media:crop.square')
            : targetAspectRatio === 0.8
            ? t('media:crop.portrait')
            : targetAspectRatio === 0.5625
            ? t('media:crop.storyReel')
            : targetAspectRatio! > 1
            ? t('media:crop.landscape', { ratio: `${Math.round(targetAspectRatio! * 9)}:9` })
            : `${targetAspectRatio!.toFixed(2)}`}
        </div>
        <div className="flex gap-3">
          {!isFreeMode && (
            <>
              <button
                type="button"
                onClick={centerImage}
                className="text-blue-600 hover:text-blue-800"
              >
                {t('media:crop.center')}
              </button>
              <button
                type="button"
                onClick={resetZoom}
                className="text-blue-600 hover:text-blue-800"
              >
                {t('common:reset')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Modal-Wrapper für den Crop-Editor
 */
interface CropEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  onSave: (cropArea: CropArea, targetFormat: FormatCategory) => void | Promise<void>;
  initialFormat?: FormatCategory;
  isSaving?: boolean;
}

export function CropEditorModal({
  isOpen,
  onClose,
  imageUrl,
  onSave,
  initialFormat,
  isSaving = false,
}: CropEditorModalProps) {
  const { t } = useTranslation(['media', 'common']);
  const [selectedFormat, setSelectedFormat] = useState<FormatCategory>(
    initialFormat || FORMAT_CATEGORIES[0]
  );
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 100, height: 100, scale: 1 });

  // Reset wenn Modal geöffnet wird
  useEffect(() => {
    if (isOpen) {
      setSelectedFormat(initialFormat || FORMAT_CATEGORIES[0]);
      setCropArea({ x: 0, y: 0, width: 100, height: 100, scale: 1 });
    }
  }, [isOpen, initialFormat]);

  if (!isOpen) return null;

  // Berechne Aspect Ratio aus Format (null für freies Zuschneiden)
  const getAspectRatio = (format: FormatCategory): number | null => {
    if (format.id === 'free' || format.aspectRatio === 'frei') {
      return null;
    }
    const [w, h] = format.aspectRatio.split(':').map(Number);
    return w / h;
  };

  const handleSave = () => {
    onSave(cropArea, selectedFormat);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">{t('media:crop.title')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Format Selection */}
        <div className="p-4 border-b bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('media:crop.selectFormat')}:
          </label>
          <div className="flex flex-wrap gap-2">
            {FORMAT_CATEGORIES.map((format) => (
              <button
                key={format.id}
                type="button"
                onClick={() => setSelectedFormat(format)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  selectedFormat.id === format.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <span className="text-lg">{format.icon}</span>
                <div className="text-left">
                  <div className="text-sm font-medium">{format.nameDe}</div>
                  <div className="text-xs text-gray-500">{format.aspectRatio}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Crop Editor */}
        <div className="p-4">
          <ImageCropEditor
            key={selectedFormat.id}
            imageUrl={imageUrl}
            targetAspectRatio={getAspectRatio(selectedFormat)}
            onCropChange={setCropArea}
          />

          <p className="text-sm text-gray-500 mt-4 text-center">
            {selectedFormat.description}
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('common:saving')}
              </>
            ) : (
              t('media:crop.apply')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
